import { useCallback, useState } from 'react';
import {
  useCancelTradeOfferMutation,
  useLazyGetMyCommittedQuantitiesQuery,
} from 'store/services/tradeOffers/tradeOffersApi';
import { useHaveListQuantitySummaryQuery } from 'store/services/collectionList/haveList/haveListApi';
import { CommitmentConflict, findCardIdConflicts } from 'utils/commitmentConflicts';
import { parseOfferError } from 'utils/offerErrors';

export interface SendFlowConflictCard {
  cardId?: number | string;
  name: string;
  quantity?: number;
}

/**
 * Commitment conflict check for the existing Send Cards surfaces (SendPage2,
 * TradeSidebar, MobileTradeDrawer). Without it a user can commit a card to a
 * pending trade offer and then async-send the same copy — same bug class as
 * the builder, second surface.
 *
 * `check(cards)` returns true when the send may proceed. On a real shortfall it
 * stores the conflicts (render CommitmentConflictDialog from them) and returns
 * false. The check is best-effort by design: if the committed-quantities read
 * fails, the send proceeds — the offer accept path's atomic re-validation is
 * the backstop, and blocking all sends on a read hiccup would be worse.
 */
export const useCommitmentConflictCheck = () => {
  const [fetchCommitted] = useLazyGetMyCommittedQuantitiesQuery();
  const { data: quantitySummary } = useHaveListQuantitySummaryQuery();
  const [cancelOffer] = useCancelTradeOfferMutation();

  const [conflicts, setConflicts] = useState<CommitmentConflict[]>([]);
  const [isCancellingOffer, setIsCancellingOffer] = useState(false);

  const clearConflicts = useCallback(() => setConflicts([]), []);

  const check = useCallback(
    async (cards: SendFlowConflictCard[]): Promise<boolean> => {
      try {
        const committed = await fetchCommitted(undefined, false).unwrap();
        const items = cards
          .map((card) => ({
            cardId: Number(card.cardId),
            quantity: card.quantity ?? 1,
            cardName: card.name,
          }))
          .filter((item) => Number.isFinite(item.cardId) && item.cardId > 0);
        const found = findCardIdConflicts(
          items,
          committed.byCardKey,
          quantitySummary?.quantitiesByCardId ?? {}
        );
        if (found.length > 0) {
          setConflicts(found);
          return false;
        }
        return true;
      } catch (err) {
        console.error('Commitment conflict check failed (proceeding):', err);
        return true;
      }
    },
    [fetchCommitted, quantitySummary]
  );

  /** Cancels the conflicting pending offer(s); true when all cancellations succeeded */
  const cancelConflictingOffers = useCallback(async (): Promise<boolean> => {
    setIsCancellingOffer(true);
    try {
      const offerIds = Array.from(new Set(conflicts.flatMap((c) => c.offerIds)));
      for (const offerId of offerIds) {
        await cancelOffer(offerId).unwrap();
      }
      setConflicts([]);
      return true;
    } catch (err) {
      console.error('Failed to cancel conflicting offer:', parseOfferError(err).message);
      return false;
    } finally {
      setIsCancellingOffer(false);
    }
  }, [conflicts, cancelOffer]);

  return { conflicts, clearConflicts, check, cancelConflictingOffers, isCancellingOffer };
};
